import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Home } from 'lucide-react';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const Login = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const validateForm = () => {
        if (!formData.email) {
            toast.error('Email is required');
            return false;
        }
        if (!formData.password) {
            toast.error('Password is required');
            return false;
        }
        const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
        if (!emailRegex.test(formData.email)) {
            toast.error('Please enter a valid email address');
            return false;
        }
        return true;
    };

    const handleLoginSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setLoading(true);
        try {
            await login(formData);
            toast.success('Logged in successfully!');
            navigate('/dashboard');
        } catch (error) {
            console.error('Login error:', error);
            const message = error.response?.data?.message || 'Login failed. Please check your credentials.';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-white relative flex items-center justify-center px-4 overflow-hidden">
            <SEO
                title="Client Login"
                description="Securely login to your MehareTech account."
            />

            {/* Background Aesthetic */}
            <div className="absolute inset-0 z-0">
                <div className="absolute top-1/4 -left-1/4 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[120px]" />
                <div className="absolute bottom-1/4 -right-1/4 w-[500px] h-[500px] bg-purple-500/5 rounded-full blur-[120px]" />
            </div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="w-full max-w-[440px] relative z-10"
            >
                <div className="bg-white/80 backdrop-blur-2xl p-8 sm:p-12 rounded-[2.5rem] border border-slate-200 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.08)] relative overflow-hidden group">
                    {/* Inner Decorative Glow */}
                    <div className="absolute -top-24 -right-24 w-48 h-48 bg-primary/5 rounded-full blur-3xl group-hover:bg-primary/10 transition-colors duration-700" />

                    <div className="relative z-10">
                        {/* Back to Home Button */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.3 }}
                            className="mb-8"
                        >
                            <Link
                                to="/"
                                className="inline-flex items-center gap-2 text-slate-400 hover:text-primary transition-all duration-300 group/home"
                            >
                                <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center group-hover/home:bg-primary/10 group-hover/home:scale-110 transition-all">
                                    <Home size={14} className="group-hover/home:text-primary transition-colors" />
                                </div>
                                <span className="font-black text-[10px] uppercase tracking-[0.2em]">Return Home</span>
                            </Link>
                        </motion.div>

                        <div className="mb-10">
                            <h1 className="text-3xl font-black text-slate-900 mb-2 tracking-tight">Welcome <span className="text-gradient">Back.</span></h1>
                            <p className="text-slate-500 text-sm font-medium">Access your personalized workspace</p>
                        </div>

                        <form onSubmit={handleLoginSubmit} className="space-y-5">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 ml-1">Email Identifier</label>
                                <div className="relative group/input">
                                    <div className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-400 group-focus-within/input:bg-primary/10 group-focus-within/input:text-primary transition-all">
                                        <Mail size={16} />
                                    </div>
                                    <input
                                        type="email"
                                        name="email"
                                        required
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder="Enter your email"
                                        className="w-full pl-16 pr-4 py-4 bg-slate-50/50 border border-slate-200 rounded-2xl focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all text-slate-900 font-semibold text-sm placeholder:text-slate-300"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 ml-1">Secure Key</label>
                                <div className="relative group/input">
                                    <div className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-400 group-focus-within/input:bg-primary/10 group-focus-within/input:text-primary transition-all">
                                        <Lock size={16} />
                                    </div>
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        required
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        placeholder="Your password"
                                        className="w-full pl-16 pr-12 py-4 bg-slate-50/50 border border-slate-200 rounded-2xl focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all text-slate-900 font-semibold text-sm placeholder:text-slate-300"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 hover:text-primary transition-colors p-1"
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                                <div className="text-right px-1">
                                    <Link to="/forgot-password" size={16} className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400 hover:text-primary transition-colors">Lost Access?</Link>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-4 bg-slate-900 text-white font-black rounded-2xl shadow-xl shadow-slate-200 hover:bg-slate-800 active:scale-[0.98] transition-all flex items-center justify-center gap-3 group disabled:opacity-50 text-xs uppercase tracking-[0.2em]"
                            >
                                {loading ? 'Validating...' : 'Authenticate'}
                                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                            </button>
                        </form>

                        <div className="mt-10 pt-8 border-t border-slate-100 text-center">
                            <p className="text-slate-400 text-[11px] font-semibold uppercase tracking-wider">
                                New to MehareTech? <Link to="/register" className="text-primary font-black hover:underline underline-offset-4 ml-1">Create Account</Link>
                            </p>
                        </div>
                    </div>
                </div>

                {/* Footer Subtle Text */}
                <p className="text-center mt-8 text-slate-300 text-[9px] font-black uppercase tracking-[0.4em]">© 2024 MehareTech Solutions</p>
            </motion.div>
        </div>
    );
};

export default Login;
